import React, { useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback
} from 'reactstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { VenueData } from '../types';
import CustomDatePicker from '../../../../components/common/CustomDatePicker/CustomDatePicker';
import GoogleMapPicker from './GoogleMapPicker';

type VenueModalProps = {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (venue: VenueData) => void;
  isLoading?: boolean;
  venue?: VenueData;
  countries: any[];
  eventStartDate?: string;
  eventEndDate?: string;
  isSingleDayEvent?: boolean;
};

const VenueModal: React.FC<VenueModalProps> = ({
  isOpen,
  toggle,
  onSubmit,
  isLoading = false,
  venue,
  countries = [],
  eventStartDate,
  eventEndDate,
  isSingleDayEvent
}) => {
  const schema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string()
        .required('Venue name is required')
        .min(2, 'Venue name must be at least 2 characters')
        .max(200, 'Venue name must be less than 200 characters'),
      addressLine1: Yup.string()
        .max(200, 'Address line 1 must be less than 200 characters'),
      addressLine2: Yup.string()
        .max(200, 'Address line 2 must be less than 200 characters'),
      country: Yup.string(),
      state: Yup.string()
        .max(100, 'State must be less than 100 characters'),
      city: Yup.string()
        .max(100, 'City must be less than 100 characters'),
      zipCode: Yup.string()
        .max(20, 'Zip code must be less than 20 characters')
        .test('zip-format', 'Please enter a valid zip code format', function(value) {
          if (!value) return true;
          return /^[A-Za-z0-9\s\-]+$/.test(value);
        }),
      eventDate: Yup.string()
        .test('date-check', 'Invalid event date', function(value) {
          if (!value || !eventStartDate) return true;
          const parseDate = (dateStr: string) => {
            if (!dateStr) return null;
            if (dateStr.includes('-')) {
              const parts = dateStr.split('-');
              if (parts[0].length === 4) {
                return new Date(dateStr);
              } else {
                return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
              }
            }
            return new Date(dateStr);
          };
          const vDate = parseDate(value);
          const sDate = parseDate(eventStartDate);
          if (!vDate || !sDate) return true;
          vDate.setHours(0, 0, 0, 0);
          sDate.setHours(0, 0, 0, 0);
          const isSingle = isSingleDayEvent || !eventEndDate;
          if (isSingle) {
            if (vDate.getTime() !== sDate.getTime()) {
              const displayDate = sDate.toLocaleDateString('en-GB');
              return this.createError({ message: `Venue date must match event date (${displayDate})` });
            }
          } else {
            const eDate = parseDate(eventEndDate);
            if (eDate) {
              eDate.setHours(0, 0, 0, 0);
              if (vDate < sDate || vDate > eDate) {
                const displayStart = sDate.toLocaleDateString('en-GB');
                const displayEnd = eDate.toLocaleDateString('en-GB');
                return this.createError({ message: `Venue date must be between event start (${displayStart}) and end (${displayEnd})` });
              }
            }
          }
          return true;
        }),
      latitude: Yup.number(),
      longitude: Yup.number(),
      formattedAddress: Yup.string(),
    });
  }, [eventStartDate, eventEndDate, isSingleDayEvent]);

  // Initialize formik with validation
  const validation = useFormik({
    initialValues: {
      name: venue?.name || '',
      addressLine1: venue?.addressLine1 || '',
      addressLine2: venue?.addressLine2 || '',
      country: venue?.country || '',
      state: venue?.state || '',
      city: venue?.city || '',
      zipCode: venue?.zipCode || '',
      eventDate: venue?.eventDate || '',
      latitude: venue?.latitude || undefined,
      longitude: venue?.longitude || undefined,
      formattedAddress: venue?.formattedAddress || '',
    },
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: (values) => {
      const formData: VenueData = {
        id: venue?.id || Date.now().toString(),
        name: values.name,
        venueName: values.name,
        addressLine1: values.addressLine1,
        addressLine2: values.addressLine2,
        country: values.country,
        state: values.state,
        city: values.city,
        zipCode: values.zipCode,
        eventDate: values.eventDate,
        latitude: values.latitude,
        longitude: values.longitude,
        formattedAddress: values.formattedAddress,
      };
      onSubmit(formData);
    }
  });

  // Reset form when modal opens/closes or venue changes
  useEffect(() => {
    if (!isOpen) {
      validation.resetForm();
    }
  }, [isOpen, venue]);

  return (
    <Modal modalClassName="quick-v2-glass-modal" isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>
        <h4 className="mb-0">{venue ? "Edit Venue" : "Add New Venue"}</h4>
      </ModalHeader>
      <Form onSubmit={validation.handleSubmit}>
        <ModalBody className="px-4">
          {/* Venue Name Field */}
          {/* <FormGroup>
            <Label className="form-label">
              Venue Name<span className="text-danger"> *</span>
            </Label>
            <Input
              name="name"
              type="text"
              placeholder="Enter venue name"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name}
              invalid={
                validation.touched.name &&
                !!validation.errors.name
              }
            />
            {validation.touched.name &&
              validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </FormGroup> */}

          {/* Event Date Field */}
          <FormGroup>
            <Label className="form-label">Event Date</Label>
            <CustomDatePicker
              value={validation.values.eventDate || ""}
              onChange={(val) => {
                validation.setFieldValue("eventDate", val);
                validation.setFieldTouched("eventDate", true);
              }}
              placeholder="Select event date"
            />
            {validation.touched.eventDate && validation.errors.eventDate ? (
              <div className="text-danger small mt-1">
                {validation.errors.eventDate}
              </div>
            ) : null}
          </FormGroup>

          {/* Google Maps Location Picker */}
          <GoogleMapPicker
            value={{
              latitude: validation.values.latitude,
              longitude: validation.values.longitude,
              formattedAddress: validation.values.formattedAddress,
              name: validation.values.name,
            }}
            onChange={(location) => {
              console.log("location", location);
              validation.setFieldValue("name", location.name);
              validation.setFieldValue("latitude", location.latitude);
              validation.setFieldValue("longitude", location.longitude);
              validation.setFieldValue(
                "formattedAddress",
                location.formattedAddress
              );
              validation.setFieldValue("addressLine1", location.addressLine1);
              validation.setFieldValue("addressLine2", location.addressLine2);
              validation.setFieldValue("country", location.country);
              validation.setFieldValue("state", location.state);
              validation.setFieldValue("city", location.city);
              validation.setFieldValue("zipCode", location.zipCode);
            }}
            onBlur={() => validation.setFieldTouched("formattedAddress", true)}
            invalid={
              validation.touched.formattedAddress &&
              !!validation.errors.formattedAddress
            }
            error={validation.errors.formattedAddress}
          />
         {validation.values.name && (
          <div className="selected-location mt-3">
            <div className="card">
              <div className="card-body">
                <h6 className="mb-2">Selected Location</h6>
                <div className="location-details">
                  <p className="mb-1">
                    <i className="fa-solid fa-location-dot me-2"></i>
                    {validation.values.name}
                  </p>

                  {validation.values.addressLine1 && (
                    <p className="mb-1 text-muted small">
                      <i className="fa-solid fa-map-marker-alt me-2"></i>
                      {validation.values.addressLine1}
                    </p>
                  )}

                  {(validation.values.city ||
                    validation.values.state ||
                    validation.values.country) && (
                    <p className="mb-0 text-muted small">
                      <i className="fa-solid fa-city me-2"></i>
                      {validation.values.city}
                      {validation.values.state && `, ${validation.values.state}`}
                      {validation.values.country && `, ${validation.values.country}`}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-secondary me-2"
            color="secondary"
            type="button"
            onClick={toggle}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            color="primary"
            type="submit"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : venue ? "Update Venue" : "Add Venue"}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default VenueModal;
